import { EVENT_SPECS } from "./eventSpecs";
import { semitoneToFreq } from "@/audio/core/noteTable";
import {
  CHAOS_ANTICIPATION_MAX_MS,
  CHAOS_ANTICIPATION_MIN_MS,
  MAX_AUDIBLE_FREQ_HZ,
  MAX_RECIPE_PEAK_GAIN,
  MIN_AUDIBLE_FREQ_HZ,
  SAFE_MAX_DURATION_MS,
  SAFE_MAX_PEAK_GAIN,
} from "@/audio/core/recipeValidation";
import type { GainEnvelope, NoiseLayer, SoundEventId, SynthLayer, ToneLayer, Voice } from "@/audio/core/types";

/** How much of the shared `intensityTier` becomes actual peak linear gain - real headroom is deliberately left below 1.0 so the master gain/limiter in `AudioBackend.ts` still have room to work with. */
const PEAK_GAIN_SCALE = 0.5;
const MIN_PEAK_GAIN = 0.04;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function clampFreq(hz: number): number {
  return clamp(hz, MIN_AUDIBLE_FREQ_HZ, MAX_AUDIBLE_FREQ_HZ);
}

/**
 * Combines a palette-independent `EventSpec` with a `Voice` into a
 * concrete, playable `SynthLayer` - the single place synthesis parameters
 * get computed, so every voice's recipe for the same event id shares the
 * same loudness/duration intent and only differs in timbre. Pure and
 * deterministic: calling this twice for the same `(eventId, voice)`
 * always returns an equal recipe. Production's `cBase.ts` calls this with
 * the approved `MINIMAL_PREMIUM_VOICE`; the QA lab's `palettes.ts` calls
 * it with all three voices for A/B comparison - this function itself
 * carries no palette identity (no `paletteId` field anywhere), only the
 * caller decides which voice to build with.
 */
export function buildRecipe(eventId: SoundEventId, voice: Voice): SynthLayer {
  const spec = EVENT_SPECS[eventId];

  const startFreqHz = clampFreq(semitoneToFreq(voice.tonicHz, spec.baseSemitone));
  const endFreqHz = clampFreq(semitoneToFreq(voice.tonicHz, spec.baseSemitone + spec.glideSemitones));
  const tones: ToneLayer[] = [{ shape: voice.everydayShape, startFreqHz, endFreqHz, mix: 1 }];
  if (spec.harmonic) {
    const harmStartHz = clampFreq(semitoneToFreq(voice.tonicHz, spec.baseSemitone + voice.harmonicDetuneSemitones));
    const harmEndHz = clampFreq(semitoneToFreq(voice.tonicHz, spec.baseSemitone + spec.glideSemitones + voice.harmonicDetuneSemitones));
    tones.push({ shape: voice.richShape, startFreqHz: harmStartHz, endFreqHz: harmEndHz, mix: 0.45 });
  }

  let noise: NoiseLayer | undefined;
  const noiseMix = clamp(spec.noiseAmount * voice.roughnessNoiseBoost, 0, 1);
  if (noiseMix > 0.01) {
    noise = { filterType: voice.noiseFilterType, filterFreqHz: clampFreq(spec.noiseFreqHz * voice.noiseBrightness), filterQ: voice.noiseQ, mix: noiseMix };
  }

  const baseAttackMs = spec.isLoopSample ? 25 : spec.durationMs <= 100 ? 3 : spec.durationMs <= 250 ? 8 : 16;
  const attackMs = clamp(baseAttackMs * voice.attackScale, 2, spec.durationMs * 0.35);
  const decayMs = clamp(spec.durationMs * 0.2, 4, spec.durationMs * 0.4);
  const sustainMs = clamp(spec.durationMs * 0.15, 0, spec.durationMs * 0.3);
  let releaseMs = Math.max(spec.durationMs - attackMs - decayMs - sustainMs, spec.durationMs * 0.2) * voice.releaseScale;
  const sustainLevel = spec.isLoopSample ? 0.55 : spec.harmonic ? 0.3 : 0.12;

  let peakGain = clamp(PEAK_GAIN_SCALE * spec.intensityTier * voice.loudnessTrim, MIN_PEAK_GAIN, MAX_RECIPE_PEAK_GAIN);

  let durationMs = Math.round(attackMs + decayMs + sustainMs + releaseMs);

  // Locked semantic caps - re-applied here, after the voice's own
  // attack/release scaling, so SAFE and CHAOS anticipation hold their
  // required shape regardless of which voice built them.
  if (eventId === "safe" && durationMs > SAFE_MAX_DURATION_MS) {
    durationMs = SAFE_MAX_DURATION_MS;
    releaseMs = Math.max(5, durationMs - attackMs - decayMs - sustainMs);
  }
  if (eventId === "safe") {
    peakGain = Math.min(peakGain, SAFE_MAX_PEAK_GAIN);
  }
  if (eventId === "chaosAnticipation") {
    durationMs = clamp(durationMs, CHAOS_ANTICIPATION_MIN_MS, CHAOS_ANTICIPATION_MAX_MS);
    releaseMs = Math.max(5, durationMs - attackMs - decayMs - sustainMs);
  }

  const envelope: GainEnvelope = { attackMs, decayMs, sustainLevel, sustainMs, releaseMs, peakGain };
  const delayTaps = spec.shimmer ? voice.shimmerTaps : undefined;
  const pan = clamp(spec.pan * voice.panWidth, -1, 1);
  const perceptualLoudness = clamp(spec.intensityTier * voice.loudnessTrim, 0, 1);

  return { durationMs, tones, noise, envelope, pan, delayTaps, isLoopSample: spec.isLoopSample, perceptualLoudness };
}
